import {Component, OnDestroy, OnInit} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Observable, Subscription, combineLatest } from "rxjs";
import {DeviceStatus, EntityStatusResponse, Location, LocationDetails, Point} from "src/app/models/dashboard";
import { BackendService } from "src/app/services/backend/backend.service";
import { SiteFilterService } from "src/app/services/site-filter.service";
import { StoreService } from "src/app/services/store.service";
import { RSocket, RSocketClient } from "rsocket-core";
import { BINARY, ViewMode } from "src/app/config/constants";
import { OAuthService } from 'angular-oauth2-oidc';
import {EntityType} from "../../../models/api/entity-type";
import {AuthorizationService} from "../../../services/authorization.service";

const SUMMARY = "summary";
const DETAILED = "detailed";
@Component({
    selector: "cc-location-pinned-view",
    templateUrl: "./pinned-view.component.html",
    styleUrls: ["./pinned-view.component.scss"],
    standalone: false,
})
export class LocationPinnedViewComponent implements OnInit, OnDestroy {
    locationDetailsList: LocationDetails[];
    locations: Location[];
    locationPointsArray: Point[] = [];
    entityStatus: { [key: string]: DeviceStatus } = {};

    pointsObj: { [key: string]: { [key: string]: Point[] } } = {};
    category = "location";
    isOverrideResetOn: boolean = false;
    userHasWritePermission$: Observable<boolean>;
    view: string = ViewMode.PINNED;
    cardType: string = SUMMARY;
    username: string = "";
    noOfPinnedLocations: number = null;
    selectedSite: { siteId: string; customerId: string };

    private mainSubscription: Subscription;
    private polledPointsValuesSubscription: Subscription;
    private rSocketClient: RSocketClient;
    entityOverriddenStatuses: { [key: string]: boolean };

    constructor(
        private backendService: BackendService,
        private activatedRoute: ActivatedRoute,
        private siteFilterService: SiteFilterService,
        private storeService: StoreService,
        private authService: OAuthService,
        private authorizationService: AuthorizationService,
    ) {}

    ngOnInit(): void {
        this.userHasWritePermission$ = this.storeService.observable;

        this.storeService.canRemoveOverrideChanged$.subscribe((value) => {
            this.isOverrideResetOn = value;
        });

        this.username = this.authorizationService.getUsername();

        this.mainSubscription = combineLatest([
            this.activatedRoute.paramMap,
            this.siteFilterService.siteFilterChanged$,
        ]).subscribe(([routeParamMap, selectedSite]) => {
            this.selectedSite = selectedSite;
            let siteId = selectedSite?.siteId;

            if (!siteId) {
                return;
            }

            this.getPinnedLocations();
        });
    }

    ngOnDestroy() {
        if (this.mainSubscription) {
            this.mainSubscription.unsubscribe();
        }

        if (this.polledPointsValuesSubscription) {
            this.polledPointsValuesSubscription.unsubscribe();
        }

        this.closeRsocket();
    }

    callKpiPolling(siteId: string, datapointIdListFilter: string[]) {
        ////////
        // Create an instance of a rSocketClient
        this.rSocketClient = this.backendService.getRSocketClient();
        // Open the connection
        this.polledPointsValuesSubscription = this.rSocketClient
            .connect()
            .subscribe({
                onComplete: (socket: RSocket) => {
                    let requestStream = "request-stream-get-datapoint-list2";
                    socket
                        .requestStream({
                            data: {
                                siteId: siteId,
                                datapointIdListFilter: datapointIdListFilter,
                                //TODO get token from authentication
                                jwtToken: this.authService.getIdToken(),
                                interaction: "Request",
                            }, // null is a must if it does not include a message payload, else the Spring server side will not be matched.
                            metadata:
                                String.fromCharCode(requestStream.length) +
                                requestStream,
                        })
                        .subscribe({
                            onComplete: () => console.log("complete"),
                            onError: (error) => {
                                console.log(
                                    "Connection has been closed due to:: " +
                                        error
                                );
                            },
                            onNext: (payload) => {
                                if (this.cardType === SUMMARY) {
                                    this.mapLocationKPI(payload.data);
                                }
                                if (this.cardType === DETAILED) {
                                    this.mapLocationPoints(payload.data);
                                }
                            },
                            onSubscribe: (subscription) => {
                                subscription.request(1000000);
                            },
                        });
                },
                onError: (error) => {
                    console.log(
                        "Connection has been refused due to:: " + error
                    );
                },
                onSubscribe: (cancel) => {
                    /* call cancel() to abort */
                },
            });
    }

    mapLocationPoints(point: any) {
        //do not update locked values;

        if (
            Object.keys(this.storeService.getLockedPoints()).includes(point.id)
        ) {
            return;
        }

        let pointToUpdateArr = this.locationPointsArray.filter(
            (locationPoint) => locationPoint.pointUUID === point.id
        );

        if (pointToUpdateArr?.length) {
            pointToUpdateArr = pointToUpdateArr.map(pointToUpdate => {
                pointToUpdate.extensions = point.extensions;
                pointToUpdate.presentValue = point.lastRecordedValue;
                pointToUpdate.lastRecordedValueMask = point.lastRecordedValueMask;
                pointToUpdate.signalType = point.signalType;
                pointToUpdate.type = point.type;
                pointToUpdate.units = point.units;
                pointToUpdate.isWritable = this.isPointWritable(pointToUpdate);
                pointToUpdate.isOverridden = point.isOverridden;
                pointToUpdate.activeOverrides = point.activeOverrides;

                if (point.signalType === BINARY) {
                    pointToUpdate.presentValue = point.lastRecordedValue
                        ? true
                        : false;
                }
                return pointToUpdate;
            })
        }
    }

    mapLocationKPI(point: any) {
        for (let location of this.locations) {
            const kpi = location.selectedKpi;
            if (!!kpi && kpi.pointUUID === point.id) {
                kpi.presentValue = point.lastRecordedValue;
                kpi.signalType = point.signalType;
                kpi.type = point.type;
                kpi.units = point.units;

                if (point.signalType === BINARY) {
                    kpi.presentValue = point.lastRecordedValue ? true : false;
                }
            }
        }
    }

    showSummary() {
        this.closeRsocket();
        this.locationPointsArray = [];

        if (this.locations?.length) {
            this.locations.forEach((location) => {
                this.locationPointsArray.push(location.selectedKpi);
            });

            this.callKpiPolling(
                this.selectedSite.siteId,
                this.locations
                    .flatMap((value) => value.selectedKpi)
                    .map((value: any) => value.pointUUID)
            );
        } else {
            this.getPinnedLocations();
        }
        this.cardType = SUMMARY;
    }

    showDetailed() {
        this.closeRsocket();
        this.locationPointsArray = [];

        if (this.locationDetailsList?.length) {
            this.locationDetailsList.forEach((locationDetails) => {
                this.locationPointsArray.push(
                    ...locationDetails.setpointsAndParametersDatapointsList,
                    ...locationDetails.commandsDatapointsList,
                    ...locationDetails.sensorsAndStatusDatapointsList,
                    ...locationDetails.alarmsDatapointsList
                );
            });

            this.callKpiPolling(
                this.selectedSite.siteId,
                this.locationPointsArray.map((point) => point.pointUUID)
            );
        } else {
			this.getPinnedLocationsDetails();
        }
        this.cardType = DETAILED;
    }

    toggleCardState(event: any) {
        if (event.target.checked) {
            this.showSummary();
        }
        if (!event.target.checked) {
            this.showDetailed();
        }
    }

    onCardPinToggle(pinnedStatus: "PINNED" | "UNPINNED") {
        if (pinnedStatus === "PINNED") {
            this.noOfPinnedLocations++;
        } else {
            this.noOfPinnedLocations--;
        }
    }

    private loadEntityStatus(locations: Location[]) {
        const params = {
            ...this.selectedSite,
            entityIds: locations.map((location) => location.id),
            entityType: EntityType.LOCATION,
        }
        this.backendService.getEntitiesStatus(params).subscribe((response: EntityStatusResponse) => this.entityStatus = response.entityStatus);
    }

    private getPinnedLocations() {
		this.backendService
                .getPinnedLocations(this.username, this.selectedSite)
                .subscribe((response: any[]) => {
                    this.locations = response;
                    this.loadEntityStatus(this.locations);
                    this.loadEntityOverriddenStatus();
                    this.noOfPinnedLocations = this.locations.length;

                    this.locations.forEach((location) => {
                        this.locationPointsArray.push(location.selectedKpi);
                    });

                    this.callKpiPolling(
                        this.selectedSite.siteId,
                        response
                            .flatMap((value) => value.selectedKpi)
                            .map((value: any) => value.pointUUID)
                    );
                });
	}

	private getPinnedLocationsDetails() {
		const pinnedLocationIds = this.locations.map(
			(location) => location.id
		);

		this.backendService
                .getPinnedLocationsDetailes(
                    this.username,
                    this.selectedSite,
                    pinnedLocationIds
                )
                .subscribe((response: LocationDetails[]) => {
                    this.locationDetailsList = response;
                    this.locationDetailsList.forEach((locationDetails) => {
						const pointsArray = [...locationDetails.setpointsAndParametersDatapointsList,
                            ...locationDetails.commandsDatapointsList,
                            ...locationDetails.sensorsAndStatusDatapointsList,
							...locationDetails.alarmsDatapointsList];
                        this.locationPointsArray.push(
                            ...pointsArray
                        );

						this.pointsObj[locationDetails.label] = {[locationDetails.label]: pointsArray};
                    });

                    this.callKpiPolling(
                        this.selectedSite.siteId,
                        this.locationPointsArray.map(
                            (point) => point.pointUUID
                        )
                    );
                });
	}

    private isPointWritable(point: Point) {
        const pointMainClass = point.class.split("_").pop();
        if (
            ["Command", "Parameter", "Setpoint", "Limit"].includes(
                pointMainClass
            )
        )
            return true;
        if (["Alarm", "Sensor", "Status"].includes(pointMainClass))
            return false;
    }

    private closeRsocket() {
        if (this.rSocketClient) {
            this.rSocketClient.close();
        }
    }

    private loadEntityOverriddenStatus() {
        this.backendService.loadEntityOverriddenStatus(this.locations, EntityType.LOCATION, this.selectedSite).subscribe(response => {
            this.entityOverriddenStatuses = response.entityOverriddenStatuses;
        });
    }

    protected readonly EntityType = EntityType;
}
